import { useState, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { GainMapLoader } from '@monogrid/gainmap-js/webgpu'
import { EquirectangularReflectionMapping } from 'three/webgpu'

// Loads the webp + gainmap + metadata triplet on a WebGPURenderer.
//
// drei's <Environment files={...}> cannot be used for this: it selects its loader purely by
// file extension (useEnvironment.js getLoader), and .webp always routes to the WebGL build of
// @monogrid/gainmap-js, whose QuadRenderer calls the WebGL-only readRenderTargetPixels().
// That throws on a WebGPURenderer and takes down the whole <CanvasImpl> tree.
//
// gainmap-js ships a separate WebGPU build under the "@monogrid/gainmap-js/webgpu" export
// subpath — same decode, but a TSL MeshBasicNodeMaterial and readRenderTargetPixelsAsync.
// drei has no reference to that subpath, so we drive the loader ourselves and hand the
// finished texture to <Environment map={...}>, which skips getLoader entirely.
//
// Keeps the 88 KB webp asset and true HDR range; no .hdr conversion needed.
export function useGainMapEnvironment(files) {
  const gl = useThree(state => state.gl)
  const [texture, setTexture] = useState(null)

  useEffect(() => {
    // The WebGPU GainMapLoader requires a WebGPURenderer. On a WebGL renderer this hook
    // should not be used at all — the caller falls back to drei's own loader.
    if (!gl?.isWebGPURenderer) return

    let cancelled = false
    let result = null

    const loader = new GainMapLoader(gl)

    loader.load(
      files,
      (quadRenderer) => {
        if (cancelled) {
          quadRenderer.dispose(true)
          return
        }
        result = quadRenderer
        const tex = quadRenderer.renderTarget.texture
        tex.mapping = EquirectangularReflectionMapping
        setTexture(tex)
      },
      undefined,
      (err) => {
        if (!cancelled) setTexture(null)
        throw err
      },
    )

    return () => {
      cancelled = true
      setTexture(null)
      // Disposes the decoded render target and the decoder material's textures.
      result?.dispose(true)
    }
  }, [gl, files])

  return texture
}
